import React, { useMemo, useState } from 'react';
import { apis, hooks, models } from "@vote4dance/judging-sdk";
import { _T } from "../src/Localization";
import { App as AntdApp, Button, Card, Col, Empty, Row, Space, Typography, List } from "antd";
import dayjs from "dayjs";
import { ProductStatus } from "@vote4dance/judging-schema";
var CafeCompetitionProductsSource = models.cafe.CafeCompetitionProductsSource;
var CafeCompetitionUserOrdersSource = models.cafe.CafeCompetitionUserOrdersSource;
var CafeCompetitionOrdersSource = models.cafe.CafeCompetitionOrdersSource;
var Api = apis.Api;
var useSession = hooks.sessions.useSession;
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SyncOutlined, DeleteOutlined } from "@ant-design/icons";
const Stream = hooks.streams.Stream;
const { Text } = Typography;
/**
 * Lightweight cafe order panel:
 * - Shows active products
 * - One-click ordering with a confirmation dialog
 */
export const CafeOrderSimple = ({ competitionId, title }) => {
    var _a;
    const { modal, notification } = AntdApp.useApp();
    const [products, loadingProducts] = Stream(competitionId ? CafeCompetitionProductsSource(competitionId) : undefined);
    const sessionState = useSession();
    const currentUserId = (_a = sessionState === null || sessionState === void 0 ? void 0 : sessionState.userInfo) === null || _a === void 0 ? void 0 : _a.user_id;
    const [orders, loadingOrders] = Stream(currentUserId ? CafeCompetitionUserOrdersSource(competitionId, currentUserId) : undefined);
    const [submitting, setSubmitting] = useState(false);
    const activeProducts = useMemo(() => (products || []).filter(p => p.product_status === ProductStatus.Active), [products]);
    const renderStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return React.createElement(CheckCircleOutlined, { style: { color: '#52c41a' } });
            case 'cancelled':
                return React.createElement(CloseCircleOutlined, { style: { color: '#ff4d4f' } });
            case 'claimed':
                return React.createElement(SyncOutlined, { spin: true, style: { color: '#faad14' } });
            default:
                return React.createElement(ClockCircleOutlined, { style: { color: '#1677ff' } });
        }
    };
    const handleOrder = async (product) => {
        if (!competitionId)
            return;
        const name = product.product_name || _T('cafe.orders.product');
        modal.confirm({
            title: _T('common.confirm'),
            content: _T('cafe.orders.placeOrder'),
            okText: _T('common.confirm'),
            cancelText: _T('common.close'),
            onOk: async () => {
                setSubmitting(true);
                try {
                    await Api.create(`${CafeCompetitionOrdersSource(competitionId).url}/${product.product_id}`, {
                        order_quantity: 1
                    });
                    notification.success({ message: _T('common.added', { name }) });
                }
                catch (err) {
                    notification.error({ message: err.message || _T('common.error') });
                }
                finally {
                    setSubmitting(false);
                }
            }
        });
    };
    const handleDelete = async (order) => {
        if (!competitionId || !currentUserId || order.order_status !== 'pending')
            return;
        const name = order.product_name || _T('cafe.orders.product');
        modal.confirm({
            title: _T('common.delete.title', { name }),
            content: _T('common.delete.content', { name }),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await Api.destroy(`${CafeCompetitionOrdersSource(competitionId).url}/${order.order_id}`);
                    notification.success({ message: _T('common.delete.success', { name }) });
                }
                catch (err) {
                    notification.error({ message: err.message || _T('common.error') });
                }
            }
        });
    };
    return (React.createElement(Card, { style: { maxWidth: 700 } },
        React.createElement(Space, { orientation: "vertical", style: { width: '100%' }, size: "middle" },
            activeProducts.length === 0 ? (React.createElement(Empty, { description: _T('cafe.empty') })) : (React.createElement(Row, { gutter: [8, 8] }, activeProducts.map(product => (React.createElement(Col, { xs: 24, sm: 12, md: 8, key: product.product_id },
                React.createElement(Card, { hoverable: true, size: "small", style: { height: '100%' }, styles: { body: { height: '100%', display: 'flex' } }, loading: loadingProducts || submitting },
                    React.createElement("div", { style: { display: 'flex', flexDirection: 'column', width: '100%', height: '100%' } },
                        React.createElement(Space, { orientation: "vertical", size: 4, style: { width: '100%' } },
                            React.createElement(Text, { strong: true }, product.product_name),
                            product.product_description && (React.createElement(Text, { type: "secondary", ellipsis: true }, product.product_description)),
                            product.product_updated_at && (React.createElement(Text, { type: "secondary", style: { fontSize: 12 } }, dayjs.utc(product.product_updated_at).local().format('LLL')))),
                        React.createElement("div", { style: { flex: 1 } }),
                        React.createElement(Button, { type: "primary", block: true, disabled: submitting, onClick: () => handleOrder(product) }, _T('cafe.orders.placeOrder'))))))))),
            orders && orders.length > 0 &&
                React.createElement(Card, { size: "small", loading: loadingOrders },
                    React.createElement(List, { dataSource: orders, renderItem: (order) => (React.createElement(List.Item, { actions: order.order_status === 'pending'
                                ? [
                                    React.createElement(Button, { key: "delete", type: "text", icon: React.createElement(DeleteOutlined, null), danger: true, onClick: () => handleDelete(order) })
                                ]
                                : undefined },
                            React.createElement(Space, { orientation: "vertical", size: 2, style: { width: '100%' } },
                                React.createElement(Text, { strong: true }, order.product_name),
                                React.createElement(Space, { size: 6 },
                                    renderStatusIcon(order.order_status),
                                    React.createElement(Text, { type: "secondary" }, order.order_created_at ? dayjs.utc(order.order_created_at).local().format('LLL') : '-'))))) })))));
};
